// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import * as authService from '../services/authService';
import { getCurrentUser } from '../services/userService';
import { listOrganizations, listSites } from '../services/omsService';
import { useShellStore } from '../stores/shellStore';
import { MOCK_PERSONAS } from '../config/mockPersonas';
import { STATIC_SITES } from '../config/sites';
import type { UserData, MeResponse, UserRole, SiteSummary, OmsSite } from '../types';

/** Project an OMS site onto the flat SiteSummary shape the shell consumes. */
const toSiteSummary = (s: OmsSite): SiteSummary => ({
  site_id: s.siteId,
  site_key: s.siteKey,
  org_id: s.orgId,
  org_key: s.orgKey,
  label: s.label,
  region_id: s.regionId,
  region_key: s.regionKey,
});

export interface AuthContextValue {
  user: UserData | null;
  meResponse: MeResponse | null;
  effectiveRole: UserRole | null;
  permissions: string[];
  sites: SiteSummary[];
  isLoading: boolean;
  isAdmin: boolean;
  refetchMe: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const mockRole = useShellStore(s => s.mockRole);
  const [user, setUser] = useState<UserData | null>(() => authService.getUser());
  const [meResponse, setMeResponse] = useState<MeResponse | null>(null);
  const [omsSites, setOmsSites] = useState<SiteSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const results = await Promise.allSettled([authService.fetchAndStoreMe(), getCurrentUser()]);

      const userData = results[0].status === 'fulfilled' ? results[0].value : null;
      const me = results[1].status === 'fulfilled' ? results[1].value : null;

      if (userData) setUser(userData);
      if (me) setMeResponse(me);
      // A 401 during hydration raises the session-expired modal (handled in
      // the api layer); no force-navigate here, which previously bounced the
      // user to the home page.
    } finally {
      setIsLoading(false);
    }

    // Source the site switcher (and the site id used for the alarms API) from
    // OMS: list the organizations we can see, then fan out to each org's sites.
    // Non-blocking — the shell renders while this resolves and the dropdown
    // fills in when it completes.
    try {
      const orgs = await listOrganizations();
      const siteLists = await Promise.all(orgs.map(org => listSites(org.id).catch(() => [])));
      setOmsSites(siteLists.flat().map(toSiteSummary));
    } catch (err) {
      console.error('[OMS] Failed to load organizations/sites for the site switcher —', err);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }
    fetchMe();
  }, [isAuthenticated, fetchMe]);

  // Only honor mock roles in development — prevents UI privilege
  // escalation via persisted localStorage in production.
  const effectiveMockRole = process.env.NODE_ENV === 'development' ? mockRole : null;

  const value = useMemo<AuthContextValue>(() => {
    // When a mock role is active, use the full persona
    if (effectiveMockRole) {
      const persona = MOCK_PERSONAS[effectiveMockRole];

      // Build mock sites from persona's assigned site IDs
      const mockSites: SiteSummary[] = persona.siteIds.map(siteId => {
        const staticSite = STATIC_SITES.find(s => s.id === siteId);
        return {
          site_id: siteId,
          site_key: staticSite?.siteKey ?? siteId,
          org_id: staticSite?.orgId ?? '',
          org_key: staticSite?.orgKey ?? '',
          label: staticSite?.label ?? siteId,
          region_id: '',
          region_key: staticSite?.regionKey ?? '',
        };
      });

      const mockUser: UserData = {
        user_id: persona.user_id,
        email: persona.email,
        first_name: persona.first_name,
        last_name: persona.last_name,
        org_id: null,
        org_name: null,
        default_role: persona.role,
        user_group_ids: [],
        effective_permissions: persona.permissions,
      };

      return {
        user: mockUser,
        meResponse,
        effectiveRole: persona.role,
        permissions: persona.permissions,
        sites: mockSites,
        isLoading: false,
        isAdmin: persona.role === 'admin',
        refetchMe: fetchMe,
      };
    }

    // Real mode: sites come from OMS (organizations → sites). Fall back to the
    // /api/v1/me site list until the OMS fetch resolves (or if it fails).
    const effectiveRole = (meResponse?.role ?? user?.default_role ?? null) as UserRole | null;
    const permissions = user?.effective_permissions ?? meResponse?.permissions ?? [];
    const sites = omsSites.length > 0 ? omsSites : (meResponse?.sites ?? []);

    return {
      user,
      meResponse,
      effectiveRole,
      permissions,
      sites,
      isLoading,
      isAdmin: effectiveRole === 'admin',
      refetchMe: fetchMe,
    };
  }, [user, meResponse, omsSites, effectiveMockRole, isLoading, fetchMe]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
